import { useLocalization } from '@lib/localization';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import { type IFolder } from '@store/api/folderEndpoints';
import { useCallback, useEffect, useRef, useState } from 'react';
import BulkUpdateModalEmptyState from './EmptyState';
import { useBulkUpload } from './hooks/useBulkUpload';
import { ModalHeader, StatusAlert, TreeContainer } from './styles/BulkUpload.styled';
import { ROOT_ID } from './tree/TreeBuilder';
import TreeItem from './tree/TreeItem';
import { type BulkTreeItem } from './types';

interface BulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  parentFolder?: IFolder | null;
}

const collectFolderIds = (root: BulkTreeItem): Set<string> => {
  const folderIds = new Set<string>([ROOT_ID]);

  const visit = (item: BulkTreeItem) => {
    if (item.type === 'folder') folderIds.add(item.id);
    item.children.forEach(visit);
  };

  visit(root);
  return folderIds;
};

const BulkUploadModal = ({ open, onClose, parentFolder }: BulkUploadModalProps) => {
  const { t } = useLocalization();

  const bulkUpload = useBulkUpload({ parentFolder });
  const { tree } = bulkUpload;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set([ROOT_ID]));
  const previousFolderIds = useRef<Set<string>>(new Set([ROOT_ID]));

  // Keep the tree fully expanded by default. A fresh scan is the only thing
  // that turns an empty tree into a populated one (you can't re-scan without
  // resetting first), so when that happens we expand every folder. Later
  // immutable updates (checkbox, status, …) keep the tree non-null, so they
  // leave the user's manual expand/collapse choices untouched — except that a
  // renamed subtree's brand-new ids are auto-expanded so it doesn't collapse.
  useEffect(() => {
    if (!tree) {
      previousFolderIds.current = new Set([ROOT_ID]);
      return;
    }

    const currentFolderIds = collectFolderIds(tree);
    const knownFolderIds = previousFolderIds.current;
    previousFolderIds.current = currentFolderIds;

    const isFreshScan = knownFolderIds.size <= 1; // only ROOT_ID ⇒ came from the empty state

    setExpandedIds(previous => {
      if (isFreshScan) return new Set(currentFolderIds);

      const next = new Set(previous);
      currentFolderIds.forEach(id => {
        if (!knownFolderIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [tree]);

  const toggleExpand = useCallback((itemId: string) => {
    setExpandedIds(previous => {
      const next = new Set(previous);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (bulkUpload.uploading) return;
    bulkUpload.reset();
    onClose();
  }, [bulkUpload, onClose]);

  const { stats, progress, pendingRename } = bulkUpload;
  const closeLabel = bulkUpload.uploadComplete ? t('Common_Close') : t('Common_Cancel');
  const progressPercent = progress.total > 0 ? (progress.done / progress.total) * 100 : 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          <ModalHeader>
            <Box>
              <Typography variant="h5" fontWeight={600}>{t('Project_Bulk_Title')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('Project_Bulk_Subtitle')}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} disabled={bulkUpload.uploading}>
              <Close />
            </IconButton>
          </ModalHeader>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          {tree ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {/* Each count is its own key, so each takes its own singular or plural. */}
                {t('Project_Bulk_Detected', [[
                  t('Common_Folder_Count', { count: stats.folders }),
                  t('Common_Project_Count', { count: stats.projects }),
                  t('Common_Inspection_Count', { count: stats.inspections }),
                ].join(', ')])}
              </Typography>

              {bulkUpload.uploading && (
                <Box>
                  <LinearProgress variant="determinate" value={progressPercent} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {bulkUpload.currentStepLabel} ({progress.done}/{progress.total})
                  </Typography>
                </Box>
              )}

              <TreeContainer>
                {tree.children.map(child => (
                  <TreeItem
                    key={child.id}
                    item={child}
                    depth={0}
                    expandedIds={expandedIds}
                    uploadInProgress={bulkUpload.uploading}
                    retryingItemId={bulkUpload.retryingItemId}
                    onToggleExpand={toggleExpand}
                    onToggleSelection={bulkUpload.toggleItemSelection}
                    onRetry={bulkUpload.retryItem}
                    onRename={bulkUpload.renameItem}
                  />
                ))}
              </TreeContainer>

              {bulkUpload.error && (
                <StatusAlert severity="error">
                  <Typography fontWeight={500}>{bulkUpload.error}</Typography>
                </StatusAlert>
              )}

              {bulkUpload.uploadComplete && (
                <StatusAlert severity="success">
                  <Typography fontWeight={500}>{t('Project_Bulk_Finished')}</Typography>
                </StatusAlert>
              )}
            </Box>
          ) : (
            <BulkUpdateModalEmptyState
              scanning={bulkUpload.scanning}
              error={bulkUpload.error}
              onFilesPicked={bulkUpload.scanFolderInput}
              onItemsDropped={bulkUpload.scanDroppedItems}
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            borderTop: '1px solid #fafafa17',
            px: 3,
            py: 2,
            justifyContent: 'space-between',
          }}
        >
          {tree ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkUpload.includeInspections}
                  onChange={(event) => bulkUpload.setIncludeInspections(event.target.checked)}
                  disabled={bulkUpload.uploading}
                />
              }
              label={t('Project_Bulk_IncludeInspections')}
            />
          ) : <Box />}

          <Box display="flex" gap={1}>
            <Button onClick={handleClose} disabled={bulkUpload.uploading}>
              {closeLabel}
            </Button>
            {tree && !bulkUpload.uploadComplete && (
              <Button
                variant="contained"
                onClick={bulkUpload.startUpload}
                disabled={bulkUpload.uploading}
                startIcon={bulkUpload.uploading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {t('Project_Bulk_StartUpload')}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingRename)} onClose={bulkUpload.cancelRename}>
        <DialogTitle>{t('Project_Bulk_Rename_Title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingRename && t('Project_Bulk_Rename_Confirm', [pendingRename.oldName, pendingRename.newName])}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={bulkUpload.cancelRename}>{t('Common_Cancel')}</Button>
          <Button variant="contained" onClick={bulkUpload.confirmRename}>{t('Common_Update')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadModal;
